import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { useAuth } from '../../context/AuthContext';
import { useAccount } from '../../context/AccountContext';
import { useAuthPage } from '../../hooks/useAuthPage';
import {
  CREATE_COWORK_MCP_CONNECTOR,
  CREATE_SETUP_INTENT,
  ROTATE_COWORK_MCP_CREDENTIALS,
  SIGNUP_MUTATION,
  UPDATE_PUBLIC_SITE,
} from '../../graphql/mutations';
import { AGENT_GUIDES, AgentGuide, ConnectionValue, VALUE_LABELS } from '../cms/agentGuides';
import { StarterStep } from './StarterStep';
import { SwallowMark } from '../common/SwallowMark';
import { REQUEST_EMAIL_CODE, RESEND_SECONDS } from './TinycmsLogin';
import {
  BillingInterval,
  billingLine,
  chargeLine,
  money,
  planPrice,
  TinycmsPlan,
  toInterval,
  useTinycmsPlans,
} from './tinycmsPlans';
import { Loader } from '../common/PageLoader';
import { RequestAccess } from './RequestAccess';
import { copyToClipboard } from '../../lib/copyToClipboard';

/**
 * The TinyCMS signup, from the plan to a connected agent, as one journey
 * ("TinyCMS UX optimization review v3.zip", TinyCMS Signup Journey):
 *
 *   Plan → Account → Payment (paid plans only) → Site address → Starting point → Agent
 *
 * The account is created at the end of Account (Free) or Payment (paid);
 * Site address and Agent are the old first-run setup, now the tail of
 * signup. Someone signed in whose site has no address yet lands back here
 * (AppShell sends them), at Site address.
 *
 * ?plan=personal&interval=month preselects a plan and starts at Account (the
 * pricing page links this way).
 *
 * Account is invite first (a typed code, or ?invite= on the link), then the
 * site name and email, then its own "Check your email" screen for the 6-digit
 * code (the account is made with that proof). There is no password: people
 * sign in with emailed codes, and can add a password later in the app.
 */

type Step = 'plan' | 'account' | 'payment' | 'address' | 'starter' | 'agent';

const STEP_LABELS: Record<Step, string> = {
  plan: 'Plan',
  account: 'Account',
  payment: 'Payment',
  address: 'Site URL',
  starter: 'Starting point',
  agent: 'Agent',
};

const SIGNUP_STATE = gql`
  query TinycmsSignupState($id: ID!) {
    account(id: $id) {
      id
      name
      publicSlug
      publicSiteUrl
    }
    coworkMcpConnector(accountId: $id) {
      mcpUrl
      hasCredentials
      lastUsedAt
    }
    tinycmsStarter(accountId: $id)
  }
`;

const VERIFY_SIGNUP_EMAIL = gql`
  mutation VerifySignupEmail($email: String!, $code: String!) {
    verifySignupEmail(email: $email, code: $code)
  }
`;

const SLUG_AVAILABLE = gql`
  query PublicSlugAvailable($slug: String!, $accountId: ID) {
    publicSlugAvailable(slug: $slug, accountId: $accountId) {
      slug
      available
      reason
    }
  }
`;

interface Credentials {
  mcpUrl: string;
  clientId: string;
  clientSecret: string;
}

const stripePromise: Promise<Stripe | null> = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);
const isStripeTestMode =
  !process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY.startsWith('pk_test_');

/** The marketing site: www.tinycms.ai in production, the same origin locally (dev edge). */
export const MARKETING_ORIGIN = window.location.hostname.endsWith('tinycms.ai')
  ? 'https://www.tinycms.ai'
  : '';

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}$/;

/**
 * Temporary: signups need this invite code while TinyCMS is being set up.
 * Checked here only (the API doesn't enforce it); remove the field and this
 * constant to open signup.
 */
const INVITE_CODE = 'let-me-in';

/** "Rosa's Bakery" → "rosas-bakery". */
function toSlug(name: string) {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

/** Back to the top of the page. Auth pages scroll the body, not the window (useAuthPage). */
function scrollToTop() {
  window.scrollTo(0, 0);
  document.body.scrollTo?.(0, 0);
  document.getElementById('root')?.scrollTo?.(0, 0);
}

const longDate = (d: Date) =>
  d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

export function FlowHeader({ right }: { right?: React.ReactNode }) {
  return (
    <header className="flow-header">
      <a className="flow-brand" href={`${MARKETING_ORIGIN}/`}>
        <SwallowMark className="flow-mark" />
        <span>
          tinycms<span className="flow-dot">.</span>
        </span>
      </a>
      {right}
    </header>
  );
}

export function TinycmsSignupFlow() {
  useAuthPage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const { accountId, selectedAccount, refetchAccounts } = useAccount();
  const { plans, loading: plansLoading } = useTinycmsPlans();

  // Someone already signed in is finishing setup, not signing up. Decided
  // once, when auth settles, so signing up mid-flow doesn't count.
  const [resuming, setResuming] = useState<boolean | null>(null);
  useEffect(() => {
    if (!authLoading && resuming === null) setResuming(isAuthenticated);
  }, [authLoading, isAuthenticated, resuming]);

  const urlPlan = searchParams.get('plan');
  const [step, setStep] = useState<Step>(urlPlan ? 'account' : 'plan');
  const [planKey, setPlanKey] = useState(urlPlan || 'personal');
  const [interval, setInterval] = useState<BillingInterval>(
    toInterval(searchParams.get('interval'))
  );

  // Account
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // Invite-only for now: the code is typed, or comes with the link (?invite=).
  const inviteFromLink = (searchParams.get('invite') || '').trim().toLowerCase() === INVITE_CODE;
  const [inviteCode, setInviteCode] = useState(inviteFromLink ? INVITE_CODE : '');
  const [inviteOk, setInviteOk] = useState(inviteFromLink);
  // Account is two screens: the details, then the emailed code (UX review v9).
  const [acctPhase, setAcctPhase] = useState<'details' | 'code'>('details');
  // "Request access" (no invite code): takes the account form's place.
  const [requestOpen, setRequestOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [signup, { loading: creatingAccount }] = useMutation<{ signup: string }>(SIGNUP_MUTATION);
  const [created, setCreated] = useState(false);
  // Email confirmation: the code step, then the server's proof (a token).
  const [codeSentTo, setCodeSentTo] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailToken, setEmailToken] = useState<{ email: string; token: string } | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [requestEmailCode, { loading: sendingCode }] = useMutation(REQUEST_EMAIL_CODE);
  const [verifySignupEmail, { loading: checkingCode }] = useMutation<{
    verifySignupEmail: string;
  }>(VERIFY_SIGNUP_EMAIL);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  const emailConfirmed = emailToken !== null && emailToken.email === email.trim().toLowerCase();
  // A code is out for the address typed now (an empty address never counts,
  // or the button would read "Continue" until the first letter).
  const codeSent = Boolean(codeSentTo) && codeSentTo === email.trim().toLowerCase();

  // Site address
  const [addr, setAddr] = useState('');
  const [addrTouched, setAddrTouched] = useState(false);

  // Arriving from sign-in keeps its scroll position otherwise.
  useEffect(() => {
    scrollToTop();
  }, []);

  // An unknown ?plan= falls back to Personal once the plans arrive.
  useEffect(() => {
    if (plans.length && !plans.some((p) => p.key === planKey)) setPlanKey('personal');
  }, [plans, planKey]);

  // Resuming: the account's own plan, name and progress decide the step.
  const accountPlanKey = selectedAccount?.products?.find((p) => p.product === 'tinycms')?.plan;
  useEffect(() => {
    if (resuming && accountPlanKey) setPlanKey(accountPlanKey);
  }, [resuming, accountPlanKey]);

  const { data: state, refetch: refetchState } = useQuery(SIGNUP_STATE, {
    variables: { id: accountId },
    skip: !isAuthenticated || !accountId,
    fetchPolicy: 'network-only',
  });
  const account = state?.account;
  const connector = state?.coworkMcpConnector;
  // A site with an address but no pages and no starter yet is at Starting point.
  const starterState = state?.tinycmsStarter as
    | { id: string | null; pageCount: number }
    | undefined;
  const needsStarter = Boolean(starterState && !starterState.id && starterState.pageCount === 0);
  // The New site modal's "I'll do it later" skips the agent after the starter.
  const skipAgent = searchParams.get('agent') === 'later';
  const decided = useRef(false);
  useEffect(() => {
    if (!resuming || decided.current || !account) return;
    decided.current = true;
    if (!name && account.name && !account.name.includes('@')) {
      setName(account.name);
      setAddr(toSlug(account.name));
    }
    if (!account.publicSlug) setStep('address');
    else if (needsStarter) setStep('starter');
    else if (!connector?.lastUsedAt && !skipAgent) setStep('agent');
    else navigate('/today', { replace: true });
  }, [resuming, account, connector, needsStarter, skipAgent, name, navigate]);

  const plan: TinycmsPlan | undefined = plans.find((p) => p.key === planKey);
  const price = plan ? planPrice(plan, interval) : 0;
  const free = plan ? price === 0 : planKey === 'free';
  const steps: Step[] = free
    ? ['plan', 'account', 'address', 'starter', 'agent']
    : ['plan', 'account', 'payment', 'address', 'starter', 'agent'];
  const idx = Math.max(0, steps.indexOf(step));
  // Signed in: the plan, account and card are settled from here on.
  const accountMade = Boolean(resuming) || created;
  const pastAccount = accountMade || idx > steps.indexOf('account');

  const trialDays = plan?.trialPeriodDays || 0;
  const trialEnd = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + trialDays);
    return d;
  }, [trialDays]);

  const go = (next: Step) => {
    setErrors({});
    setStep(next);
    scrollToTop();
  };

  /** Creates the account (with the saved card on a paid plan), then signs in. */
  const createAccount = async (paymentMethod?: string, verifiedToken?: string) => {
    const emailVerification = verifiedToken || (emailConfirmed ? emailToken?.token : undefined);
    if (!plan) return false;
    try {
      const { data } = await signup({
        variables: {
          email,
          ...(emailVerification ? { emailVerification } : {}),
          accountName: name.trim(),
          plan: plan.key,
          products: ['tinycms'],
          billingInterval: interval,
          ...(paymentMethod ? { paymentName: email, paymentMethod } : {}),
        },
      });
      if (!data?.signup) throw new Error('Sign up failed');
      login(data.signup);
      setCreated(true);
      if (!addrTouched) setAddr(toSlug(name));
      go('address');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setErrors({
        submit: /already exists/i.test(message)
          ? 'exists'
          : paymentMethod
            ? 'Sign up failed. Please try again. Your card was not charged.'
            : 'Sign up failed. Please try again.',
      });
      return false;
    }
  };

  const errorText = (err: unknown, fallback: string) =>
    err instanceof Error && err.message ? err.message : fallback;

  const sendEmailCode = async () => {
    try {
      await requestEmailCode({ variables: { email: email.trim(), purpose: 'sign_up' } });
      setCodeSentTo(email.trim().toLowerCase());
      setEmailCode('');
      setCooldown(RESEND_SECONDS);
      setErrors((x) => ({ ...x, email: '', code: '' }));
      setAcctPhase('code');
    } catch (err) {
      setErrors((x) => ({ ...x, email: errorText(err, 'The code couldn’t be sent. Try again.') }));
    }
  };

  const applyInvite = () => {
    if (inviteCode.trim().toLowerCase() !== INVITE_CODE) {
      setErrors((x) => ({
        ...x,
        invite: inviteCode.trim() ? 'That invite code isn’t right' : 'Add your invite code',
      }));
      return false;
    }
    setInviteOk(true);
    setErrors((x) => ({ ...x, invite: '' }));
    return true;
  };

  const submitAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (acctPhase === 'details') {
      if (!inviteOk && !applyInvite()) return;
      const next: Record<string, string> = {};
      if (!name.trim()) next.name = 'Add a name for your site';
      if (!email) next.email = 'Add your email';
      else if (!/\S+@\S+\.\S+/.test(email)) next.email = 'That doesn’t look like an email address';
      setErrors(next);
      if (Object.keys(next).length) return;
      // Already confirmed this address (came back from Payment): straight on.
      if (emailConfirmed) {
        if (free) await createAccount();
        else go('payment');
        return;
      }
      // A code is still out for this address: back to it, else send one.
      if (codeSent) setAcctPhase('code');
      else await sendEmailCode();
      return;
    }

    // The code screen.
    if (!emailConfirmed) {
      if (emailCode.replace(/\D/g, '').length !== 6) {
        setErrors({ code: 'Enter the 6-digit code from the email' });
        return;
      }
      try {
        const { data } = await verifySignupEmail({
          variables: { email: email.trim(), code: emailCode },
        });
        if (!data?.verifySignupEmail) return;
        setEmailToken({ email: email.trim().toLowerCase(), token: data.verifySignupEmail });
        if (free) await createAccount(undefined, data.verifySignupEmail);
        else go('payment');
      } catch (err) {
        setErrors({ code: errorText(err, 'That code didn’t work.') });
      }
      return;
    }
    if (free) await createAccount();
    else go('payment');
  };

  // Auth settles before the flow decides; a signed-in visit waits for its account.
  if (resuming === null || (resuming && !account) || (plansLoading && !plans.length)) {
    return (
      <div className="flow">
        <FlowHeader />
        <Loader label="Loading" className="flow-loading" />
      </div>
    );
  }

  const headerRight = pastAccount ? (
    email ? (
      <span className="flow-header-note">{email}</span>
    ) : null
  ) : (
    <span className="flow-header-note">
      <span className="flow-m-hide">Already have an account? </span>
      <button type="button" className="flow-link" onClick={() => navigate('/login')}>
        Log in
      </button>
    </span>
  );

  const stepOf = `Step ${idx + 1} of ${steps.length}`;

  return (
    <div className="flow">
      <FlowHeader right={headerRight} />
      <main className="flow-main">
        <div className="flow-bar" aria-hidden="true">
          {steps.map((s, i) => (
            <span key={s} data-on={i <= idx ? 'true' : undefined} />
          ))}
        </div>
        <nav className="flow-rail" aria-label="Signup steps">
          {steps.map((s, i) => {
            const done = i < idx;
            const now = i === idx;
            // Before the account exists any earlier step can be revisited.
            const canGo = done && !accountMade;
            return (
              <button
                key={s}
                type="button"
                className="flow-rail-step"
                data-state={done ? 'done' : now ? 'now' : 'next'}
                disabled={!canGo}
                onClick={() => canGo && go(s)}
              >
                <span className="flow-rail-num">{done ? '✓' : i + 1}</span>
                <span>{STEP_LABELS[s]}</span>
              </button>
            );
          })}
        </nav>

        <section className="flow-section" data-wide={step === 'starter' ? 'true' : undefined}>
          {step === 'plan' ? (
            <PlanStep
              stepOf={stepOf}
              plans={plans}
              planKey={planKey}
              onPlan={setPlanKey}
              interval={interval}
              onInterval={setInterval}
              onContinue={() => go('account')}
            />
          ) : null}

          {step === 'account' && requestOpen ? (
            <RequestAccess initialEmail={email} onClose={() => setRequestOpen(false)} />
          ) : null}

          {step === 'account' && !requestOpen && acctPhase === 'details' ? (
            <form onSubmit={submitAccount} noValidate>
              <div className="flow-step-of">{stepOf}</div>
              <h1 className="flow-h1">Create your account</h1>
              <p className="flow-lede">
                You sign in with just your email. We send you a code each time, so there’s no
                password to remember.
              </p>

              {/* The invite comes first; the fields open once it's accepted. */}
              {inviteOk ? (
                <div className="flow-invite-ok">
                  <span>✓</span>
                  <span>{inviteFromLink ? 'Invite accepted from your link' : `Invite accepted · ${inviteCode.trim()}`}</span>
                  <button type="button" className="flow-invite-change" onClick={() => setInviteOk(false)}>
                    Change
                  </button>
                </div>
              ) : (
                <div className="flow-invite">
                  <div className="flow-invite-text">
                    <span className="flow-invite-title">TinyCMS is invite-only for now</span>
                    <span className="flow-invite-sub">Enter the invite code you were given to get started.</span>
                  </div>
                  <div className="flow-invite-row">
                    <input
                      className="flow-input"
                      data-error={errors.invite ? 'true' : undefined}
                      value={inviteCode}
                      placeholder="Invite code"
                      autoComplete="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      autoFocus
                      onChange={(e) => {
                        setInviteCode(e.target.value);
                        setErrors((x) => ({ ...x, invite: '' }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          applyInvite();
                        }
                      }}
                    />
                    <button type="button" className="flow-btn-quiet flow-invite-apply" onClick={applyInvite}>
                      Apply
                    </button>
                  </div>
                  {errors.invite ? (
                    <span className="flow-error">{errors.invite}</span>
                  ) : (
                    <span className="flow-hint">
                      No code?{' '}
                      <button type="button" className="flow-link" onClick={() => setRequestOpen(true)}>
                        Request access
                      </button>
                    </span>
                  )}
                </div>
              )}

              <div className="flow-fields flow-acct-fields" data-dim={inviteOk ? undefined : 'true'}>
                <label className="flow-field">
                  <span className="flow-label">Site or company name</span>
                  <input
                    className="flow-input"
                    data-error={errors.name ? 'true' : undefined}
                    value={name}
                    placeholder="Rosa’s Bakery"
                    autoComplete="organization"
                    onChange={(e) => {
                      setName(e.target.value);
                      setErrors((x) => ({ ...x, name: '' }));
                    }}
                  />
                  {errors.name ? <span className="flow-error">{errors.name}</span> : null}
                </label>
                <label className="flow-field">
                  <span className="flow-label">Email</span>
                  <input
                    className="flow-input"
                    data-error={errors.email ? 'true' : undefined}
                    type="email"
                    value={email}
                    placeholder="you@company.com"
                    autoComplete="email"
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors((x) => ({ ...x, email: '', submit: '' }));
                    }}
                  />
                  {errors.email ? <span className="flow-error">{errors.email}</span> : null}
                </label>
              </div>
              <SubmitError error={errors.submit} email={email} onLogin={() => navigate('/login')} />
              <button
                type="submit"
                className="flow-btn"
                data-dim={inviteOk ? undefined : 'true'}
                disabled={sendingCode}
              >
                {sendingCode ? 'Sending a code…' : 'Continue'}
              </button>
              <p className="flow-fine">
                Next, we’ll email you a 6-digit code to confirm it’s you.
                <br />
                By continuing you agree to the{' '}
                <a href="https://www.tinycms.ai/terms" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="https://www.tinycms.ai/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                .
              </p>
            </form>
          ) : null}

          {step === 'account' && !requestOpen && acctPhase === 'code' ? (
            <form onSubmit={submitAccount} noValidate>
              <div className="flow-step-of">{stepOf}</div>
              <h1 className="flow-h1">Check your email</h1>
              <p className="flow-lede">
                We sent a 6-digit code to <strong className="flow-strong">{codeSentTo}</strong>. It works
                for 10 minutes.
              </p>
              <input
                className="flow-input flow-code-big"
                data-error={errors.code ? 'true' : undefined}
                value={emailCode}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                maxLength={6}
                aria-label="6-digit code"
                autoFocus
                onChange={(e) => {
                  setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setErrors((x) => ({ ...x, code: '' }));
                }}
              />
              {errors.code ? <div className="flow-error flow-code-error">{errors.code}</div> : null}
              <div className="flow-code-actions">
                <button
                  type="button"
                  className="flow-link flow-resend"
                  disabled={cooldown > 0 || sendingCode}
                  onClick={() => void sendEmailCode()}
                >
                  {cooldown > 0 ? `Send a new code in ${cooldown}s` : 'Send a new code'}
                </button>
                <button type="button" className="flow-link" onClick={() => setAcctPhase('details')}>
                  Use a different email
                </button>
              </div>
              <SubmitError error={errors.submit} email={email} onLogin={() => navigate('/login')} />
              <button
                type="submit"
                className="flow-btn"
                data-dim={emailCode.length === 6 ? undefined : 'true'}
                disabled={creatingAccount || checkingCode}
              >
                {creatingAccount
                  ? 'Creating your account…'
                  : checkingCode
                    ? 'Checking…'
                    : free
                      ? 'Create free account'
                      : 'Continue to payment'}
              </button>
              <p className="flow-fine">
                Each time you sign in, we’ll email you a new code. Prefer a password? Add one later in
                Account → Profile.
              </p>
            </form>
          ) : null}

          {step === 'payment' && plan ? (
            <Elements stripe={stripePromise}>
              <PaymentStep
                stepOf={stepOf}
                plan={plan}
                interval={interval}
                email={email}
                emailVerification={emailConfirmed ? emailToken?.token : undefined}
                trialEnd={trialEnd}
                busy={creatingAccount}
                submitError={errors.submit}
                onLogin={() => navigate('/login')}
                onCard={createAccount}
              />
            </Elements>
          ) : null}

          {step === 'address' ? (
            <AddressStep
              stepOf={stepOf}
              free={free}
              accountId={accountId}
              siteName={name.trim() || account?.name || ''}
              addr={addr}
              onAddr={(v) => {
                setAddr(v);
                setAddrTouched(true);
              }}
              onSaved={async () => {
                await Promise.all([refetchState(), refetchAccounts()]);
                go('starter');
              }}
            />
          ) : null}

          {step === 'starter' ? (
            <StarterStep
              stepOf={stepOf}
              accountId={accountId}
              siteName={name.trim() || account?.name || ''}
              siteAddr={account?.publicSlug || addr}
              onDone={async () => {
                await refetchState();
                if (skipAgent) {
                  scrollToTop();
                  navigate('/today');
                } else go('agent');
              }}
            />
          ) : null}

          {step === 'agent' ? (
            <AgentStep
              stepOf={stepOf}
              accountId={accountId}
              existingConnector={connector}
              onDone={() => {
                scrollToTop();
                navigate('/today');
              }}
            />
          ) : null}
        </section>

        {/* The starting point takes the width: no plan aside there. */}
        {step !== 'plan' && step !== 'starter' && plan ? (
          <aside className="flow-aside">
            <div className="flow-aside-head">
              <span>Your plan</span>
              {!accountMade ? (
                <button type="button" className="flow-link" onClick={() => go('plan')}>
                  Change
                </button>
              ) : null}
            </div>
            <div className="flow-aside-plan">{plan.label}</div>
            {!resuming ? (
              <>
                <div className="flow-aside-price">
                  {free ? 'Free, no card needed' : billingLine(plan, interval)}
                </div>
                {!free && trialDays ? (
                  <div className="flow-aside-trial">
                    Free until {longDate(trialEnd)}. Then {chargeLine(plan, interval)}.
                  </div>
                ) : null}
              </>
            ) : null}
          </aside>
        ) : null}
      </main>
    </div>
  );
}

function SubmitError({
  error,
  email,
  onLogin,
}: {
  error?: string;
  email: string;
  onLogin: () => void;
}) {
  if (!error) return null;
  return (
    <p className="flow-error flow-submit-error">
      {error === 'exists' ? (
        <>
          An account with {email} already exists.{' '}
          <button type="button" className="flow-link" onClick={onLogin}>
            Log in instead
          </button>
        </>
      ) : (
        error
      )}
    </p>
  );
}

function IntervalToggle({
  interval,
  onInterval,
}: {
  interval: BillingInterval;
  onInterval: (v: BillingInterval) => void;
}) {
  return (
    <div className="flow-seg" role="radiogroup" aria-label="Billing">
      {(['month', 'year'] as BillingInterval[]).map((v) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={interval === v}
          data-active={interval === v ? 'true' : undefined}
          onClick={() => onInterval(v)}
        >
          {v === 'month' ? 'Monthly' : 'Yearly'}
          {v === 'year' ? <span className="flow-save">Save 25%</span> : null}
        </button>
      ))}
    </div>
  );
}

function PlanStep({
  stepOf,
  plans,
  planKey,
  onPlan,
  interval,
  onInterval,
  onContinue,
}: {
  stepOf: string;
  plans: TinycmsPlan[];
  planKey: string;
  onPlan: (key: string) => void;
  interval: BillingInterval;
  onInterval: (v: BillingInterval) => void;
  onContinue: () => void;
}) {
  const selected = plans.find((p) => p.key === planKey);
  const trial = Math.max(0, ...plans.map((p) => p.trialPeriodDays || 0));
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onContinue();
      }}
    >
      <div className="flow-step-of">{stepOf}</div>
      <h1 className="flow-h1">Choose a plan</h1>
      <p className="flow-lede">
        {trial
          ? `Start free, or try any paid plan free for ${trial} days. Switch at any time.`
          : 'Start free, or pick a paid plan. Switch at any time.'}
      </p>
      <IntervalToggle interval={interval} onInterval={onInterval} />
      <div className="flow-plans" role="radiogroup" aria-label="Plan">
        {plans.map((p) => {
          const on = p.key === planKey;
          const pr = planPrice(p, interval);
          return (
            <div
              key={p.key}
              role="radio"
              aria-checked={on}
              tabIndex={0}
              className="flow-plan"
              data-active={on ? 'true' : undefined}
              onClick={() => onPlan(p.key)}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  onPlan(p.key);
                }
              }}
            >
              <div className="flow-plan-row">
                <span className="flow-radio" />
                <div className="flow-plan-text">
                  <div className="flow-plan-name">{p.label}</div>
                  <div className="flow-plan-summary">{p.summary}</div>
                  <div className="flow-plan-sub">
                    {!pr
                      ? 'No card needed'
                      : `${interval === 'year' ? `${money(pr * 12)} billed yearly` : 'Billed monthly'}${
                          p.trialPeriodDays ? ` · ${p.trialPeriodDays}-day trial` : ''
                        }`}
                  </div>
                </div>
                <div className="flow-plan-price">
                  {money(pr)}
                  <span>/mo</span>
                </div>
              </div>
              {on ? (
                <ul className="flow-features">
                  {p.features.map((f) => (
                    <li key={f}>
                      <span aria-hidden="true">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>
      <button type="submit" className="flow-btn" disabled={!selected}>
        {selected ? `Continue with ${selected.label}` : 'Continue'}
      </button>
    </form>
  );
}

function PaymentStep({
  stepOf,
  plan,
  interval,
  email,
  emailVerification,
  trialEnd,
  busy,
  submitError,
  onLogin,
  onCard,
}: {
  stepOf: string;
  plan: TinycmsPlan;
  interval: BillingInterval;
  email: string;
  /** The confirmed-email token: lets this step ask for a SetupIntent before an account exists. */
  emailVerification?: string;
  trialEnd: Date;
  busy: boolean;
  submitError?: string;
  onLogin: () => void;
  onCard: (paymentMethod: string) => Promise<boolean>;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [createSetupIntent] = useMutation<{ setupIntent: { clientSecret: string } }>(
    CREATE_SETUP_INTENT
  );
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [cardError, setCardError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchSetupIntent = useCallback(async () => {
    setLoadFailed(false);
    try {
      const { data } = await createSetupIntent({
        variables: { email: email.trim().toLowerCase(), emailVerification },
      });
      setClientSecret(data?.setupIntent?.clientSecret || null);
      if (!data?.setupIntent?.clientSecret) setLoadFailed(true);
    } catch {
      setLoadFailed(true);
    }
  }, [createSetupIntent, email, emailVerification]);

  useEffect(() => {
    void fetchSetupIntent();
  }, [fetchSetupIntent]);

  const cardStyle = useMemo(() => {
    const css = getComputedStyle(document.documentElement);
    const v = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback;
    return {
      style: {
        base: {
          fontSize: '16px',
          color: v('--ink-ink', '#0d0f12'),
          fontFamily: 'Geist, system-ui, sans-serif',
          '::placeholder': { color: v('--ink-ink3', '#7a8190') },
        },
        invalid: { color: v('--ink-red', '#d33'), iconColor: v('--ink-red', '#d33') },
      },
      hidePostalCode: true,
    };
  }, []);

  const trialDays = plan.trialPeriodDays || 0;
  const charge = chargeLine(plan, interval);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;
    const card = elements.getElement(CardElement);
    if (!card) return;
    setCardError('');
    setConfirming(true);
    try {
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card, billing_details: { email } },
      });
      if (error) {
        setCardError(error.message || 'Your card couldn’t be saved. Try again.');
        return;
      }
      if (setupIntent?.status === 'succeeded' && setupIntent.payment_method) {
        const ok = await onCard(setupIntent.payment_method as string);
        // The confirmed intent is used up; a retry needs a fresh one.
        if (!ok) void fetchSetupIntent();
      }
    } finally {
      setConfirming(false);
    }
  };

  const working = confirming || busy;
  return (
    <form onSubmit={submit} noValidate>
      <div className="flow-step-of">{stepOf}</div>
      <h1 className="flow-h1">{trialDays ? 'Start your free trial' : 'Add your card'}</h1>
      <p className="flow-lede">
        {trialDays
          ? `Add a card to try ${plan.label}. You won’t be charged until ${longDate(trialEnd)}, and you can cancel before then.`
          : `Add a card for ${plan.label}.`}
      </p>
      {isStripeTestMode ? (
        <div className="flow-test">
          <strong>Test mode.</strong> Use 4242 4242 4242 4242, any future date and any CVC.
        </div>
      ) : null}
      <div className="flow-field" style={{ marginTop: 20 }}>
        <span className="flow-label">Card</span>
        {loadFailed ? (
          <p className="flow-error">
            The card form didn’t load.{' '}
            <button type="button" className="flow-link" onClick={() => void fetchSetupIntent()}>
              Try again
            </button>
          </p>
        ) : (
          <div className="flow-card" data-error={cardError ? 'true' : undefined}>
            <CardElement options={cardStyle} onChange={() => setCardError('')} />
          </div>
        )}
        {cardError ? <span className="flow-error">{cardError}</span> : null}
      </div>
      <div className="flow-due">
        <span>Due today</span>
        <strong>{trialDays ? '$0.00' : charge}</strong>
      </div>
      <SubmitError error={submitError} email={email} onLogin={onLogin} />
      <button type="submit" className="flow-btn" disabled={working || !stripe || !clientSecret}>
        {working
          ? trialDays
            ? 'Starting your trial…'
            : 'Saving…'
          : trialDays
            ? `Start ${trialDays}-day free trial`
            : 'Continue'}
      </button>
      {trialDays ? (
        <p className="flow-fine">
          On {longDate(trialEnd)} you’ll be charged {charge}, then{' '}
          {interval === 'year' ? 'yearly' : 'monthly'} until you cancel in Settings → Plan &amp;
          billing. Free trials are one per person: sites you add later are billed from the day you
          add them.
        </p>
      ) : null}
    </form>
  );
}

function AddressStep({
  stepOf,
  free,
  accountId,
  siteName,
  addr,
  onAddr,
  onSaved,
}: {
  stepOf: string;
  free: boolean;
  accountId?: string;
  siteName: string;
  addr: string;
  onAddr: (v: string) => void;
  onSaved: () => Promise<void>;
}) {
  const [check, { data, loading: checking }] = useLazyQuery(SLUG_AVAILABLE, {
    fetchPolicy: 'network-only',
  });
  const [updatePublicSite, { loading: saving }] = useMutation(UPDATE_PUBLIC_SITE);
  const [saveError, setSaveError] = useState('');

  // Ask the server once typing pauses.
  useEffect(() => {
    if (!SLUG_RE.test(addr)) return;
    const t = setTimeout(() => {
      void check({ variables: { slug: addr, accountId: accountId || null } });
    }, 300);
    return () => clearTimeout(t);
  }, [addr, accountId, check]);

  const result = data?.publicSlugAvailable;
  const current = result && result.slug === addr ? result : null;
  const host = `${addr}.tinycms.ai`;
  let tone: 'ok' | 'error' | undefined;
  let status: string;
  if (!addr) status = 'Letters, numbers and hyphens';
  else if (addr.length < 2) status = 'At least 2 characters';
  else if (!SLUG_RE.test(addr)) status = 'Start with a letter or number';
  else if (!current || checking) status = 'Checking…';
  else if (current.available) {
    tone = 'ok';
    status = `${host} is available`;
  } else {
    tone = 'error';
    status = current.reason === 'reserved' ? `${addr} is reserved` : `${host} is taken`;
  }
  const ok = tone === 'ok';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ok || !accountId) return;
    setSaveError('');
    try {
      await updatePublicSite({
        variables: {
          accountId,
          input: { publicSlug: addr, publicTitle: siteName || addr, publicEnabled: true },
        },
      });
      await onSaved();
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setSaveError(
        /already in use/i.test(message)
          ? `${host} was just taken. Try another.`
          : /reserved/i.test(message)
            ? `${addr} is reserved. Try another.`
            : message || 'Your address couldn’t be saved. Try again.'
      );
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="flow-step-of">{stepOf}</div>
      <h1 className="flow-h1">Pick your site address</h1>
      <p className="flow-lede">
        {free
          ? 'This is where your site lives. You can switch to your own web address on a paid plan.'
          : 'Your site goes live here first. You can add your own web address later in Site.'}
      </p>
      <div className="flow-addr">
        <span className="flow-m-hide">https://</span>
        <input
          value={addr}
          placeholder="yourname"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          aria-label="Site address"
          onChange={(e) =>
            onAddr(
              e.target.value
                .toLowerCase()
                .replace(/[^a-z0-9-]/g, '')
                .slice(0, 63)
            )
          }
        />
        <span>.tinycms.ai</span>
      </div>
      <div className="flow-status-line" data-tone={tone}>
        <span className="flow-status-dot" />
        {status}
      </div>
      {saveError ? <p className="flow-error flow-submit-error">{saveError}</p> : null}
      <button type="submit" className="flow-btn" disabled={!ok || saving || !accountId}>
        {saving ? 'Saving…' : 'Continue'}
      </button>
    </form>
  );
}

function CopyRow({ label, value, secret }: { label: string; value: string; secret?: boolean }) {
  const [copied, setCopied] = useState(false);
  const [shown, setShown] = useState(!secret);
  const multiline = value.includes('\n');
  return (
    <div className="flow-cred">
      <span className="flow-cred-label">{label}</span>
      <div className="flow-cred-row">
        <code className="flow-cred-value" data-multiline={multiline ? 'true' : undefined}>
          {shown ? value : `${value.slice(0, 4)}${'•'.repeat(16)}`}
        </code>
        {secret ? (
          <button type="button" className="flow-cred-reveal" onClick={() => setShown((v) => !v)}>
            {shown ? 'Hide' : 'Show'}
          </button>
        ) : null}
        <button
          type="button"
          className="flow-cred-copy"
          onClick={async (e) => {
            // If the browser refuses, Show reveals the value to select.
            if (await copyToClipboard(value, e.currentTarget)) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

/**
 * What the guides can show. The address is known as soon as the site has a
 * connection; the key values only while a just-made secret is in memory.
 */
function connectionValues(
  mcpUrl: string,
  creds: Credentials | null
): Partial<Record<ConnectionValue, string>> {
  const values: Partial<Record<ConnectionValue, string>> = {
    url: mcpUrl,
    authUrl: `${mcpUrl}/oauth/authorize`,
    tokenUrl: `${mcpUrl}/oauth/token`,
    scope: 'mcp',
    // VS Code registers itself and opens the sign-in window; no key in the file.
    vscodeConfig: JSON.stringify({ servers: { tinycms: { type: 'http', url: mcpUrl } } }, null, 2),
  };
  if (creds) {
    values.clientId = creds.clientId;
    values.clientSecret = creds.clientSecret;
    // The Client ID is also a bearer token for agents that take one.
    values.token = creds.clientId;
    values.vscodeTokenConfig = JSON.stringify(
      {
        servers: {
          tinycms: {
            type: 'http',
            url: mcpUrl,
            headers: { Authorization: `Bearer ${creds.clientId}` },
          },
        },
      },
      null,
      2
    );
  }
  return values;
}

const SECRET_VALUES: ConnectionValue[] = ['clientId', 'clientSecret', 'token', 'vscodeTokenConfig'];

/** The key values, in the order the Advanced fold lists them. */
const ADVANCED_VALUES: ConnectionValue[] = [
  'clientId',
  'clientSecret',
  'token',
  'authUrl',
  'tokenUrl',
  'scope',
  'vscodeTokenConfig',
];

function AgentStep({
  stepOf,
  accountId,
  existingConnector,
  onDone,
}: {
  stepOf: string;
  accountId?: string;
  existingConnector?: {
    mcpUrl: string;
    hasCredentials: boolean;
    lastUsedAt: string | null;
  } | null;
  onDone: () => void;
}) {
  const [creds, setCreds] = useState<Credentials | null>(null);
  const [error, setError] = useState('');
  const [guide, setGuide] = useState<AgentGuide>(AGENT_GUIDES[0]);
  const [connected, setConnected] = useState(false);
  const [createConnector, { loading: creating }] = useMutation(CREATE_COWORK_MCP_CONNECTOR);
  const [rotateConnector, { loading: rotating }] = useMutation(ROTATE_COWORK_MCP_CREDENTIALS);
  const createdFor = useRef<string | null>(null);

  // A new site gets its connection the moment this step opens: that is the
  // connection the agent signs in to. One made before (someone resuming) is
  // reused as it is — the sign-in path never needs the secret.
  useEffect(() => {
    if (!accountId || creds || existingConnector?.hasCredentials) return;
    if (createdFor.current === accountId) return;
    createdFor.current = accountId;
    createConnector({ variables: { accountId, label: 'AI agent' } })
      .then(({ data }) => setCreds(data?.createCoworkMcpConnector || null))
      .catch((err) => {
        createdFor.current = null;
        setError(err instanceof Error ? err.message : 'Your connection couldn’t be set up.');
      });
  }, [accountId, creds, existingConnector, createConnector]);

  const mcpUrl = creds?.mcpUrl || existingConnector?.mcpUrl || '';

  // Connected = the agent has signed in or used the connection (lastUsedAt
  // is stamped on both). Checked every few seconds while waiting.
  const { data: poll, refetch } = useQuery(SIGNUP_STATE, {
    variables: { id: accountId },
    skip: !accountId || !mcpUrl || connected,
    fetchPolicy: 'network-only',
    pollInterval: 4000,
  });
  useEffect(() => {
    if (mcpUrl && poll?.coworkMcpConnector?.lastUsedAt) setConnected(true);
  }, [mcpUrl, poll]);
  const [checking, setChecking] = useState(false);

  const newDetails = async () => {
    if (!accountId) return;
    setError('');
    try {
      const { data } = await rotateConnector({ variables: { accountId, label: 'AI agent' } });
      setCreds(data?.rotateCoworkMcpCredentials || null);
      setConnected(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'New connection details couldn’t be made.');
    }
  };

  const values = mcpUrl ? connectionValues(mcpUrl, creds) : null;
  const agentName = guide.name;

  const row = (v: ConnectionValue) =>
    values?.[v] != null ? (
      <CopyRow
        key={v}
        label={VALUE_LABELS[v]}
        value={values[v] as string}
        secret={SECRET_VALUES.includes(v)}
      />
    ) : (
      <p key={v} className="flow-hint flow-cred-missing">
        {VALUE_LABELS[v]}: the key was shown only when it was made. Make a new one under Advanced
        below.
      </p>
    );

  return (
    <div>
      <div className="flow-step-of">{stepOf}</div>
      <h1 className="flow-h1">Connect your agent</h1>
      <p className="flow-lede">
        Paste your site’s address into your agent, then sign in with your email when it asks. Your
        agent builds your pages and posts through TinyCMS. It can’t publish anything until you
        approve it.
      </p>

      {!mcpUrl && (creating || !error) ? (
        <p className="flow-lede">Setting up your connection…</p>
      ) : null}

      {values ? (
        <>
          <div className="flow-tabs" role="tablist" aria-label="Agent">
            {AGENT_GUIDES.map((g) => (
              <button
                key={g.id}
                type="button"
                role="tab"
                aria-selected={g.id === guide.id}
                data-active={g.id === guide.id ? 'true' : undefined}
                onClick={() => setGuide(g)}
              >
                {g.name}
              </button>
            ))}
          </div>
          <ol className="flow-guide">
            {guide.steps.map((s, i) => (
              <li key={`${guide.id}-${i}`}>
                <div>{s.text}</div>
                {s.values?.map(row)}
              </li>
            ))}
          </ol>
          {guide.note ? <p className="flow-hint">{guide.note}</p> : null}
          <div className="flow-status" data-tone={connected ? 'ok' : undefined}>
            <span className="flow-status-dot" data-pulse={connected ? undefined : 'true'} />
            <span>
              {connected
                ? `Your agent is connected. Ask it for your first page, in your own words. It can’t publish anything until you approve it.`
                : checking
                  ? 'Checking…'
                  : `Waiting for ${agentName} to connect`}
            </span>
          </div>
          <details className="flow-advanced">
            <summary>Advanced: for tools that need a key</summary>
            {creds ? (
              <>
                <p className="flow-hint">
                  Most agents sign you in with your email and never need these. For one that asks
                  for a key, paste them here. Keep them private, like a password: the secret is
                  shown only now, and you can make new details in Settings → Connections.
                </p>
                {ADVANCED_VALUES.map(row)}
              </>
            ) : (
              <>
                <p className="flow-hint">
                  Most agents sign you in with your email and never need a key. Your site already
                  has one, and its secret was shown only when it was made. Make a new one to paste
                  into a tool that needs it; any tool using the old one stops working.
                </p>
                <button
                  type="button"
                  className="flow-btn-quiet"
                  disabled={rotating}
                  onClick={() => void newDetails()}
                >
                  {rotating ? 'Making…' : 'Make new connection details'}
                </button>
              </>
            )}
          </details>
        </>
      ) : null}

      {error ? <p className="flow-error flow-submit-error">{error}</p> : null}

      {mcpUrl ? (
        <button
          type="button"
          className="flow-btn"
          disabled={checking}
          onClick={async () => {
            if (connected) return onDone();
            setChecking(true);
            try {
              const { data } = await refetch();
              if (data?.coworkMcpConnector?.lastUsedAt) setConnected(true);
            } finally {
              setChecking(false);
            }
          }}
        >
          {connected ? 'Open Today' : checking ? 'Checking…' : 'Check connection'}
        </button>
      ) : null}
      {!connected ? (
        <p className="flow-fine">
          <button type="button" className="flow-link" onClick={onDone}>
            Skip for now
          </button>
          . You can connect one later in Settings → Connections.
        </p>
      ) : null}
    </div>
  );
}
